import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import {
  InteractionRequiredAuthError,
  InteractionStatus,
  SilentRequest,
} from '@azure/msal-browser';
import {
  setAccessToken,
  setLoginData,
  setUsername,
} from '../store/slices/RootSlice';
import {
  getLegacyConnectorClient,
  getPoliciesClient,
  initializeApiFactory,
  updateTokenJWTFactory,
} from './api/ClientServicesCloud';
import LoginData from '../interface/LoginData';
import setMSALLoginAxiosDefaults, {
  updateMSALToken,
} from './setMSALLoginAxiosDefaults';
import { BluAsyncDialog } from '@bludata/components';
import useLogoutMSAL from './useLogoutMSAL';
import getDecodedJWTToken from './msal/getDecodedJWTToken';
import { setTokenJWT, clearMsalCookies } from './loginMSALAuthProcess';
import loginMsalHookReducer, {
  loginMsalHookReducerInitialState,
} from './loginMsalHookReducer';
import { getExtraQueryParamsForRedirect } from './utils/getExtraQueryParamsForRedirect';
import axios from 'axios';
import { ErrorCodes } from '@bludata/fc-legacyconnector';
import { getIsImpersonation } from './msal/MsalInstance';
import localforage from 'localforage';
import {
  clearDoingLogout,
  isDoingLogoutValid,
  migrateLegacyDoingLogout,
} from './utils/doingLogoutUtils';
import { isRefreshInProgress } from './TokenRefreshGuard';
import { tryAcquireInteractionLock } from './AuthInteractionLock';

export const SESSION_TIMEOUT_KEY = 'sessionLastActivity';
export const SESSION_TIMEOUT_LIMIT = 30 * 60 * 1000; // 30 minuti in millisecondi

export const updateLastActivityTimestamp = () => {
  localStorage.setItem(SESSION_TIMEOUT_KEY, Date.now().toString());
};

/**
 * Verifica lo stato di timeout della sessione SENZA side-effects.
 *
 * @returns 'active' se il timestamp è presente e valido (entro SESSION_TIMEOUT_LIMIT)
 * @returns 'expired' se il timestamp è presente ma più vecchio di SESSION_TIMEOUT_LIMIT
 * @returns 'new' se non esiste un timestamp o non è valido (sessione nuova)
 *
 * NOTA: funzione pura, non chiama logout né aggiorna il timestamp.
 * Il chiamante decide cosa fare in base al risultato.
 */
export const checkSessionTimeout = (): 'active' | 'expired' | 'new' => {
  const raw = localStorage.getItem(SESSION_TIMEOUT_KEY);

  // Se non c'è ancora un timestamp → sessione nuova
  if (!raw) {
    return 'new';
  }

  const lastActivity = parseInt(raw, 10);
  if (isNaN(lastActivity)) {
    return 'new';
  }

  const now = Date.now();
  const passedTime = now - lastActivity;

  // Se l'utente è stato inattivo troppo a lungo → sessione scaduta
  if (passedTime > SESSION_TIMEOUT_LIMIT) {
    return 'expired';
  }

  // L'utente è ancora nei limiti → sessione attiva
  return 'active';
};

const useLoginMsal = () => {
  const userLoggedIn = useIsAuthenticated();
  const { instance: msalInstance, accounts, inProgress } = useMsal();
  const { loginData } = useSelector((state: RootState) => state.root);
  const dispatch = useDispatch<AppDispatch>();
  const loginDataLoaded = !!loginData.CodiceUtente;
  const { logout } = useLogoutMSAL();
  const timers = useRef<NodeJS.Timeout[]>([]);
  // Singleton: tiene traccia dell'unico timer di refresh attivo.
  // Ogni nuova chiamata a scheduleTokenRefresh cancella il precedente,
  // evitando che catene parallele si accumulino (wake-from-sleep, watchdog, ecc.)
  const activeRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // Ref per inProgress: permette a acquireAndScheduleToken di leggere il valore
  // corrente SENZA aggiungerlo come dependency (evita ricreazioni a cascata)
  const inProgressRef = useRef(inProgress);
  useEffect(() => {
    inProgressRef.current = inProgress;
  }, [inProgress]);

  // Ref per lo stato intermedio del reducer: permette a scheduleTokenRefresh
  // di verificare se l'app è in stato OTP/errore senza averlo come dependency
  const intermediateStateRef = useRef({
    needOtpCode: false,
    noSessionAvailable: false,
    loginError: false,
    needToChoosePDV: false,
  });
  const nomeFilialeLoggata = loginData.RagioneSociale
    ? `${loginData.RagioneSociale}: ${loginData.NomeFiliale}`
    : '';

  /**
   * Aggiorna il token nei vari layer (axios, factory, redux).
   * Funzione pura: NON gestisce attività utente né logout.
   * L'aggiornamento del timestamp di attività va fatto esplicitamente dal chiamante.
   *
   * @param newToken - il nuovo access token MSAL
   */
  const updateGlobalToken = useCallback(
    (newToken: string | undefined) => {
      updateMSALToken(newToken as string);
      updateTokenJWTFactory(newToken as string);
      dispatch(setAccessToken(newToken || ''));

      if (!newToken) {
        console.warn(
          'updateGlobalToken chiamata con un token vuoto o non definito.',
        );
      }
    },
    [dispatch],
  );

  const [state, localDispatch] = useReducer(
    loginMsalHookReducer,
    loginMsalHookReducerInitialState,
  );

  const {
    token,
    isLoadingToken,
    calledInit,
    loginError,
    needOtpCode,
    needToChoosePDV,
    callingInit,
    noSessionAvailable,
  } = state;

  const [account] = accounts;

  // Pulizia one-shot dei flag legacy al mount
  useEffect(() => {
    migrateLegacyDoingLogout();
    localStorage.removeItem('msal.logout');
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Tieni aggiornato il ref dello stato intermedio
  useEffect(() => {
    intermediateStateRef.current = {
      needOtpCode,
      noSessionAvailable,
      loginError,
      needToChoosePDV,
    };
  }, [needOtpCode, noSessionAvailable, loginError, needToChoosePDV]);

  useEffect(() => {
    if (!token) return;

    const tokenJWT = getDecodedJWTToken();
    if (tokenJWT && loginData && loginData.RagioneSociale) {
      if (tokenJWT.accessLevel !== 'company') {
        localforage.setItem(
          'terminal_FOCUSCLOUD_workplaceName',
          `${loginData.RagioneSociale}: ${loginData.NomeFiliale}`,
        );
      } else {
        localforage.setItem(
          'terminal_FOCUSCLOUD_workplaceName',
          loginData.RagioneSociale,
        );
      }
    }
  }, [loginData, nomeFilialeLoggata, token]);

  const request: SilentRequest = useMemo(() => {
    if (!account) {
      console.warn('Account non definito, il request sarà nullo.');
      return {} as SilentRequest; // O gestisci l'assenza dell'account
    }
    return {
      scopes: [
        `https://${
          import.meta.env.VITE_NX_PUBLIC_YOUR_TENANT_NAME
        }.onmicrosoft.com/focuscloud/All`,
      ], // Modifica con i tuoi scopes
      account,
    };
  }, [account]);

  const scheduleTokenRefresh = useCallback(
    (retryCount = 0): (() => void) => {
      const MAX_RETRIES = 3;
      const RETRY_BASE_DELAY = 30 * 1000; // 30 secondi

      // Cancella il timer di refresh precedente per evitare catene parallele
      if (activeRefreshTimerRef.current) {
        clearTimeout(activeRefreshTimerRef.current);
        activeRefreshTimerRef.current = null;
      }

      const decodedToken = getDecodedJWTToken();
      if (!decodedToken) {
        // Token non decodificabile: ritenta dopo un breve delay (max 2 tentativi)
        if (retryCount < 2) {
          console.warn(
            `[scheduleTokenRefresh] Token non decodificabile, ritento (${retryCount + 1}/2)...`,
          );
          const retryId = setTimeout(
            () => scheduleTokenRefresh(retryCount + 1),
            5 * 1000,
          );
          timers.current.push(retryId);
          return () => clearTimeout(retryId);
        }
        console.error(
          '[scheduleTokenRefresh] Token non decodificabile dopo 2 tentativi',
        );
        return () => null;
      }

      // Assicuriamoci che i timestamp siano gestiti in UTC
      const tokenExpiryUTC = new Date(decodedToken.exp * 1000).getTime();
      const nowUTC = new Date().getTime();

      // Calcolo dell'intervallo di refresh — clock-agnostic
      // Usa il lifetime del token (exp - iat) per non dipendere dall'orologio del client,
      // che potrebbe essere avanti/indietro di minuti causando loop di refresh
      const tokenLifetimeMs =
        (decodedToken.exp - (decodedToken.iat || decodedToken.nbf)) * 1000;
      const lifetimeBasedInterval = tokenLifetimeMs - 3 * 60 * 1000;
      const clockBasedInterval = tokenExpiryUTC - nowUTC - 3 * 60 * 1000;
      const refreshInterval = Math.min(
        5 * 60 * 1000, // Max 5 minuti (token B2C dura 15 min, serve margine abbondante)
        Math.max(lifetimeBasedInterval, clockBasedInterval),
      );

      // Se il token è quasi scaduto o già scaduto, tenta il refresh con un minimo di 10s
      // per evitare loop: MSAL potrebbe restituire lo stesso token dalla cache
      const MIN_REFRESH_INTERVAL = 10 * 1000; // 10 secondi — safety net anti-loop
      const needsImmediateRefresh = refreshInterval <= 0;
      const effectiveInterval = needsImmediateRefresh
        ? MIN_REFRESH_INTERVAL
        : refreshInterval;
      if (needsImmediateRefresh) {
        console.warn(
          '[scheduleTokenRefresh] Token quasi scaduto, tentativo di refresh tra 10s.',
        );
      }

      const refreshTimerId = setTimeout(async () => {
        // Guard: se il componente è stato smontato, non fare nulla
        if (!isMountedRef.current) return;

        // Guard: se un refresh è già in corso (da TokenRefreshGuard o altri entry-point), skip
        if (isRefreshInProgress()) {
          console.log(
            '[scheduleTokenRefresh] Refresh già in corso, ripianifica tra 10s.',
          );
          scheduleTokenRefresh(retryCount);
          return;
        }

        try {
          // Pulisce i cookie MSAL prima di acquisire il token
          clearMsalCookies();
          // Se il token è quasi scaduto, forza il refresh da B2C (non dalla cache MSAL)
          const silentRequest = needsImmediateRefresh
            ? { ...request, forceRefresh: true }
            : request;
          const refreshResponse =
            await msalInstance.acquireTokenSilent(silentRequest);
          const refreshedToken = refreshResponse.accessToken;

          if (!refreshedToken) {
            // Token nullo: ritenta con backoff (conta come retry)
            console.error('[scheduleTokenRefresh] Token aggiornato è nullo.');
            if (retryCount < MAX_RETRIES) {
              const delay = RETRY_BASE_DELAY * Math.pow(2, retryCount);
              console.warn(
                `[scheduleTokenRefresh] Retry ${retryCount + 1}/${MAX_RETRIES} tra ${delay / 1000}s`,
              );
              scheduleTokenRefresh(retryCount + 1);
            } else {
              console.error(
                '[scheduleTokenRefresh] Max retry raggiunti con token nullo, redirect a B2C',
              );
              localStorage.removeItem(SESSION_TIMEOUT_KEY);
              if (
                !tryAcquireInteractionLock(
                  'scheduleRefresh',
                  'token nullo dopo max retry',
                )
              )
                return;
              const extraParams = await getExtraQueryParamsForRedirect();
              msalInstance.acquireTokenRedirect({
                ...request,
                extraQueryParameters: extraParams,
              });
            }
            return;
          }

          localDispatch({ type: 'SET_TOKEN', payload: refreshedToken });

          // Refresh automatico → NON aggiorniamo attività utente
          updateGlobalToken(refreshedToken);

          // Pianifica il prossimo refresh (reset retry count)
          scheduleTokenRefresh(0);
        } catch (error) {
          console.error('Errore durante il refresh del token:', error);
          if (error instanceof InteractionRequiredAuthError) {
            // Verifica che l'app non sia in uno stato intermedio (OTP, scelta PDV, errore)
            // Un redirect a B2C in questi stati perderebbe lo stato React e forzerebbe
            // l'utente a rifare il login da zero
            const {
              needOtpCode,
              noSessionAvailable,
              loginError,
              needToChoosePDV,
            } = intermediateStateRef.current;
            if (
              needOtpCode ||
              noSessionAvailable ||
              loginError ||
              needToChoosePDV
            ) {
              console.warn(
                '[MSAL] Token refresh failed ma app in stato intermedio, riprovo tra 60s.',
                {
                  needOtpCode,
                  noSessionAvailable,
                  loginError,
                  needToChoosePDV,
                },
              );
              // Invece di return (che ucciderebbe la catena), schedula un retry
              const retryId = setTimeout(
                () => scheduleTokenRefresh(0),
                60 * 1000,
              );
              timers.current.push(retryId);
              return;
            }
            if (request) {
              // Pulizia timestamp stale prima del redirect a B2C
              // Al ritorno, checkSessionTimeout restituirà 'new' → nessun logout spurio
              localStorage.removeItem(SESSION_TIMEOUT_KEY);
              if (
                !tryAcquireInteractionLock(
                  'scheduleRefresh',
                  'InteractionRequired in scheduleTokenRefresh',
                )
              )
                return;
              const extraParams = await getExtraQueryParamsForRedirect();
              msalInstance.acquireTokenRedirect({
                ...request,
                extraQueryParameters: extraParams,
              });
            }
          } else {
            // Errore non-InteractionRequired (rete, BrowserAuthError, ecc.)
            // Ritenta con backoff esponenziale prima di arrendersi
            if (retryCount < MAX_RETRIES) {
              const delay = RETRY_BASE_DELAY * Math.pow(2, retryCount);
              console.warn(
                `[scheduleTokenRefresh] Errore non-interattivo, retry ${retryCount + 1}/${MAX_RETRIES} tra ${delay / 1000}s`,
              );
              const retryId = setTimeout(
                () => scheduleTokenRefresh(retryCount + 1),
                delay,
              );
              timers.current.push(retryId);
            } else {
              // Tutti i tentativi esauriti: redirect a B2C come ultima risorsa
              console.error(
                '[scheduleTokenRefresh] Max retry raggiunti, redirect a B2C',
              );
              if (request) {
                localStorage.removeItem(SESSION_TIMEOUT_KEY);
                if (
                  !tryAcquireInteractionLock(
                    'scheduleRefresh',
                    'max retry non-interattivi',
                  )
                )
                  return;
                const extraParams = await getExtraQueryParamsForRedirect();
                msalInstance.acquireTokenRedirect({
                  ...request,
                  extraQueryParameters: extraParams,
                });
              }
            }
          }
        }
      }, effectiveInterval);

      activeRefreshTimerRef.current = refreshTimerId;
      timers.current.push(refreshTimerId);

      return () => {
        clearTimeout(refreshTimerId);
        if (activeRefreshTimerRef.current === refreshTimerId) {
          activeRefreshTimerRef.current = null;
        }
        timers.current = timers.current.filter((id) => id !== refreshTimerId);
      };
    },
    [msalInstance, request, updateGlobalToken],
  );

  const acquireAndScheduleToken = useCallback(async () => {
    if (userLoggedIn) {
      localDispatch({ type: 'SET_LOADING', payload: true });

      // Pulizia flag doingLogout dopo login riuscito
      await clearDoingLogout();

      // Verifica che l'account sia disponibile prima di procedere
      if (!account) {
        console.warn(
          'Account non disponibile per acquireAndScheduleToken, attendo...',
        );
        localDispatch({ type: 'SET_LOADING', payload: false });
        return undefined;
      }

      try {
        // Acquisisce il token iniziale
        // Pulisce i cookie MSAL prima di acquisire il token
        clearMsalCookies();
        const response = await msalInstance.acquireTokenSilent(request);
        const newToken = response.accessToken;

        // ⚠️ PRIMA di settare il token nello state: verificare se la sessione è scaduta.
        // Se l'utente era inattivo per troppo tempo (es. 2-3 giorni), dobbiamo fare logout
        // SENZA procedere con init. Altrimenti si crea una sessione orfana.
        const sessionStatus = checkSessionTimeout();
        if (sessionStatus === 'expired') {
          // Guard: se l'URL contiene un auth code B2C (parametro ?code= o #code=),
          // l'utente sta tornando da un login legittimo. Il SESSION_TIMEOUT_KEY stale
          // è un falso positivo — ricreato da un altro tab mentre l'utente era su B2C
          const url = window.location.href;
          if (/[?&#]code=/.test(url)) {
            console.warn(
              '[acquireAndScheduleToken] Session expired ma redirect response presente, skip logout',
            );
            localStorage.removeItem(SESSION_TIMEOUT_KEY);
          } else {
            console.warn(
              '[acquireAndScheduleToken] Sessione scaduta per inattività, eseguo logout.',
            );
            await logout(true, 'idle-timeout');
            return undefined;
          }
        }

        localDispatch({ type: 'SET_TOKEN', payload: newToken });
        updateGlobalToken(newToken);

        // Sessione attiva o nuova → aggiorna il timestamp di attività
        updateLastActivityTimestamp();

        // Pianifica il refresh del token
        return scheduleTokenRefresh(); // Ritorna la funzione di cleanup
      } catch (error) {
        console.error('Errore durante acquireAndScheduleToken:', error);
        if (error instanceof InteractionRequiredAuthError) {
          // Pulizia timestamp stale prima del redirect a B2C
          localStorage.removeItem(SESSION_TIMEOUT_KEY);
          if (
            !tryAcquireInteractionLock(
              'acquireScheduleToken',
              'InteractionRequired in acquireAndScheduleToken',
            )
          )
            return;
          const extraParams = await getExtraQueryParamsForRedirect();
          await msalInstance.acquireTokenRedirect({
            ...request,
            extraQueryParameters: extraParams,
          });
        } else {
          // Altri errori: segnala errore di login per mostrare la pagina di errore
          console.error(
            'Errore non gestito durante acquisizione token:',
            error,
          );
          localDispatch({ type: 'SET_LOGIN_ERROR', payload: true });
        }
      } finally {
        localDispatch({ type: 'SET_LOADING', payload: false });
      }
    } else {
      // Se MSAL sta processando un redirect (HandleRedirect, Login, SsoSilent, etc.),
      // NON settare isLoadingToken a false — il loader deve restare visibile
      // fino a quando il redirect è completato e useIsAuthenticated() si aggiorna
      if (inProgressRef.current === InteractionStatus.None) {
        localDispatch({ type: 'SET_LOADING', payload: false });
      }
      // Se inProgress !== None, isLoadingToken resta true (valore iniziale)
      // → il Connector mostra il loader, non LoginPage
    }

    return undefined;
  }, [
    userLoggedIn,
    msalInstance,
    request,
    account,
    updateGlobalToken,
    scheduleTokenRefresh,
  ]);

  const refreshToken = useCallback(
    async (workplaceRef: string) => {
      localDispatch({ type: 'SET_LOADING', payload: true });

      try {
        await getPoliciesClient().setSessionAsync(workplaceRef);

        // Acquisisce il token iniziale
        // Pulisce i cookie MSAL prima di acquisire il token
        clearMsalCookies();
        const response = await msalInstance.acquireTokenSilent({
          ...request,
          forceRefresh: true,
        });
        const newToken = response.accessToken;
        localDispatch({ type: 'SET_TOKEN', payload: newToken });

        // Refresh esplicito (es. cambio PDV) → attività utente reale
        updateGlobalToken(newToken);
        updateLastActivityTimestamp();

        // Pianifica il refresh del token
        return scheduleTokenRefresh(); // Ritorna la funzione di cleanup
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          if (
            !tryAcquireInteractionLock(
              'refreshTokenPDV',
              'InteractionRequired in refreshToken',
            )
          )
            return;
          const extraParams = await getExtraQueryParamsForRedirect();
          await msalInstance.acquireTokenRedirect({
            ...request,
            extraQueryParameters: extraParams,
          });
        }
      } finally {
        window.location.reload();
        // localDispatch({ type: 'SET_LOADING', payload: false });
      }
    },
    [msalInstance, request, updateGlobalToken, scheduleTokenRefresh],
  );

  useEffect(() => {
    return () => {
      timers.current.forEach((timerId) => clearTimeout(timerId));
    };
  }, []);

  // Wake-from-sleep: al ritorno dalla sospensione, verifica se il token è scaduto
  // o prossimo alla scadenza e lo rinnova immediatamente prima che partano chiamate API
  useEffect(() => {
    if (!userLoggedIn || !loginDataLoaded) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      // Guard: se un refresh è già in corso, non accumulare catene di timer
      if (isRefreshInProgress()) {
        console.log('[wake-from-sleep] Refresh già in corso, skip.');
        return;
      }

      try {
        const decodedToken = getDecodedJWTToken();
        if (!decodedToken) return;

        const tokenExpiryUTC = decodedToken.exp * 1000;
        const nowUTC = Date.now();
        const minutesUntilExpiry = (tokenExpiryUTC - nowUTC) / (60 * 1000);

        // Se il token scade entro 3 minuti, rinnovalo subito
        if (minutesUntilExpiry <= 3) {
          console.warn(
            `[wake-from-sleep] Token scade tra ${minutesUntilExpiry.toFixed(1)} min, eseguo refresh...`,
          );
          clearMsalCookies();
          const response = await msalInstance.acquireTokenSilent({
            ...request,
            forceRefresh: true,
          });
          const newToken = response.accessToken;
          if (newToken) {
            localDispatch({ type: 'SET_TOKEN', payload: newToken });
            updateGlobalToken(newToken);
            // Ri-avvia la catena di refresh con il nuovo token
            scheduleTokenRefresh(0);
            console.log('[wake-from-sleep] Token refreshato con successo');
          }
        }
      } catch (error) {
        console.error('[wake-from-sleep] Errore durante il refresh:', error);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    userLoggedIn,
    loginDataLoaded,
    msalInstance,
    request,
    updateGlobalToken,
    scheduleTokenRefresh,
  ]);

  // Listener per l'evento 'jwt-refresh-needed': triggerato dal keepalive,
  // dal response interceptor o da altri moduli quando rilevano un JWT invalido.
  // Esegue un refresh immediato e ri-avvia la catena di refresh.
  useEffect(() => {
    if (!userLoggedIn || !loginDataLoaded) return;

    let debounceTimer: NodeJS.Timeout | null = null;

    const handleJwtRefreshNeeded = () => {
      // Debounce: se più eventi arrivano in rapida successione, esegui una sola volta
      if (debounceTimer) return;

      debounceTimer = setTimeout(async () => {
        debounceTimer = null;

        if (isRefreshInProgress()) {
          console.log('[jwt-refresh-needed] Refresh già in corso, skip');
          return;
        }

        try {
          console.warn(
            '[jwt-refresh-needed] Evento ricevuto, eseguo refresh token...',
          );
          clearMsalCookies();
          const response = await msalInstance.acquireTokenSilent({
            ...request,
            forceRefresh: true,
          });
          const newToken = response.accessToken;
          if (newToken) {
            localDispatch({ type: 'SET_TOKEN', payload: newToken });
            updateGlobalToken(newToken);
            scheduleTokenRefresh(0);
            console.log('[jwt-refresh-needed] Token refreshato con successo');
          }
        } catch (error) {
          console.error(
            '[jwt-refresh-needed] Errore durante il refresh:',
            error,
          );
        }
      }, 5000); // 5 secondi di debounce
    };

    window.addEventListener('jwt-refresh-needed', handleJwtRefreshNeeded);
    return () => {
      window.removeEventListener('jwt-refresh-needed', handleJwtRefreshNeeded);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [
    userLoggedIn,
    loginDataLoaded,
    msalInstance,
    request,
    updateGlobalToken,
    scheduleTokenRefresh,
  ]);

  // Watchdog: safety net di ultima istanza. Ogni 3 minuti verifica se il token
  // è ancora valido. Se è scaduto e nessun refresh è in corso, ne forza uno.
  // Copre il caso in cui la catena di refresh è morta per motivi imprevisti.
  useEffect(() => {
    if (!userLoggedIn || !loginDataLoaded) return;

    const watchdogInterval = setInterval(
      async () => {
        try {
          const decodedToken = getDecodedJWTToken();
          if (!decodedToken) return;

          const tokenExpiryUTC = decodedToken.exp * 1000;
          const nowUTC = Date.now();
          const secondsUntilExpiry = (tokenExpiryUTC - nowUTC) / 1000;

          // Se il token scade entro 60 secondi e nessun refresh è in corso
          if (secondsUntilExpiry <= 60 && !isRefreshInProgress()) {
            console.warn(
              `[watchdog] Token scade tra ${secondsUntilExpiry.toFixed(0)}s, nessun refresh in corso. Forzo refresh...`,
            );
            clearMsalCookies();
            const response = await msalInstance.acquireTokenSilent({
              ...request,
              forceRefresh: true,
            });
            const newToken = response.accessToken;
            if (newToken) {
              localDispatch({ type: 'SET_TOKEN', payload: newToken });
              updateGlobalToken(newToken);
              scheduleTokenRefresh(0);
              console.log('[watchdog] Token refreshato con successo');
            }
          }
        } catch (error) {
          console.error('[watchdog] Errore durante il refresh:', error);
        }
      },
      3 * 60 * 1000,
    ); // Ogni 3 minuti

    return () => clearInterval(watchdogInterval);
  }, [
    userLoggedIn,
    loginDataLoaded,
    msalInstance,
    request,
    updateGlobalToken,
    scheduleTokenRefresh,
  ]);

  // Effetto supplementare: quando inProgress diventa None e l'utente non è loggato,
  // sblocca isLoadingToken. Necessario perché acquireAndScheduleToken non ha inProgress
  // tra le sue dependency e potrebbe essere stato eseguito quando inProgress era ancora
  // Startup/HandleRedirect, lasciando isLoadingToken bloccato a true.
  useEffect(() => {
    if (
      !userLoggedIn &&
      inProgress === InteractionStatus.None &&
      isLoadingToken
    ) {
      localDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [inProgress, userLoggedIn, isLoadingToken]);

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    acquireAndScheduleToken().then((result) => {
      cleanup = result; // Assign the cleanup function if returned
    });

    return () => {
      if (cleanup) cleanup();
    };
  }, [acquireAndScheduleToken]);

  const callFOCUSInit = useCallback(
    async (otpData = {}, revokeSessionId: string | undefined) => {
      localDispatch({ type: 'SET_LOADING', payload: true });

      const handleInitError = async (error) => {
        const subtypeError = error?.subType || '';

        console.error('Errore durante init FOCUS:', error);

        if (subtypeError === ErrorCodes.NoAvailableSessions) {
          localDispatch({ type: 'SET_NO_SESSION_AVAILABLE', payload: true });
          localDispatch({ type: 'SET_LOADING', payload: false });
          return;
        } else if (subtypeError === ErrorCodes.NoSessionLicenses) {
          const retry = await BluAsyncDialog({
            title: 'Attenzione',
            message: 'La tua licenza non ha sessioni disponibili.',
            type: 'error',
            hideDeclineButton: true,
            confirmButton: 'chiudi',
          });
          if (retry) {
            logout(false, 'explicit');
          }
          return;
        } else if (subtypeError === ErrorCodes.SessionAlreadyRevoked) {
          const retry = await BluAsyncDialog({
            title: 'Attenzione',
            message:
              "È già presente una sessione in fase di revoca. Per revocare un'altra sessione è necessario attendere il completamento del processo di revoca in corso.",
            type: 'warning',
            hideDeclineButton: true,
            confirmButton: 'Ho capito',
          });
          if (retry) {
            localDispatch({ type: 'SET_NO_SESSION_AVAILABLE', payload: true });
          }
          localDispatch({ type: 'SET_LOADING', payload: false });
          return;
        }

        const needNewOtp =
          subtypeError === ErrorCodes.MissingCookieOrOtp ||
          subtypeError === ErrorCodes.WrongOtpCode ||
          subtypeError === ErrorCodes.WrongCookie;

        if (needNewOtp) {
          localDispatch({ type: 'SET_NEED_OTP_CODE', payload: true });
        } else {
          const retry = await BluAsyncDialog({
            title: 'Attenzione',
            message: 'Errore durante la procedura di login.',
            type: 'error',
            hideDeclineButton: true,
            confirmButton: 'Riprova',
          });
          if (retry) {
            logout(false, 'explicit');
          }
        }
      };

      if (revokeSessionId) {
        localDispatch({ type: 'SET_NO_SESSION_AVAILABLE', payload: false });
        localDispatch({ type: 'SET_LOADING', payload: true });
      }
      const decodedJWT = getDecodedJWTToken();

      if (
        decodedJWT &&
        decodedJWT?.accessLevel === 'company' &&
        (decodedJWT?.workplaceRef === '' ||
          decodedJWT?.workplaceRef === undefined)
      ) {
        localDispatch({ type: 'SET_NEED_TO_CHOOSE_PDV', payload: true });
        localDispatch({ type: 'SET_LOADING', payload: false });
        return;
      }

      //! Lasciare per sviluppo e debug locale
      if (import.meta.env.VITE_NX_PUBLIC_LOCALAPI) {
        axios.defaults.baseURL = import.meta.env.VITE_NX_PUBLIC_LOCALAPI;
        axios
          .post<LoginData>('/focusapi/init')
          .then((response) => {
            const decodedJWT = getDecodedJWTToken();
            dispatch(setAccessToken(token));
            dispatch(setUsername(decodedJWT?.userRef ?? ''));
            dispatch(setLoginData(response.data));
            setTokenJWT(token);
            setMSALLoginAxiosDefaults(
              token, //?token jwt bearer
              'locallogin', //?chiave dove è salvato il path per le chiamate api
              dispatch,
            );
            localDispatch({ type: 'ALL_READY' });
          })
          .catch(async (error) => {
            console.error('error', error);

            dispatch(setAccessToken(''));
            setTokenJWT('');
            localDispatch({ type: 'SET_LOGIN_ERROR', payload: true });
          })
          .finally(() => {
            localDispatch({ type: 'SET_LOADING', payload: false });
          });
        return;
      }
      //!

      getLegacyConnectorClient()
        .init(otpData as any, revokeSessionId)
        .then(async (response: LoginData) => {
          const { $FocusCloudRouteName, $FocusCloudStatus } = response;
          if ($FocusCloudStatus === 'locked-for-update') {
            if (
              await BluAsyncDialog({
                title: 'Attenzione',
                message:
                  'Il sistema è in fase di aggiornamento, riprova più tardi.',
                type: 'error',
                hideDeclineButton: true,
                confirmButton: 'Riprova',
              })
            ) {
              logout(false, 'explicit');
            }
          } else {
            const decodedJWT = getDecodedJWTToken();
            dispatch(setAccessToken(token));
            dispatch(setUsername(decodedJWT?.userRef ?? ''));
            dispatch(setLoginData(response));
            setTokenJWT(token);
            setMSALLoginAxiosDefaults(token, $FocusCloudRouteName, dispatch);
            //@ts-expect-error proprietà globale per tracking timestamp init
            window.__focusInitTimestamp = Date.now();
            localDispatch({ type: 'ALL_READY' });
          }
        })
        .catch((error: any) => handleInitError(error))
        .finally(() => {
          localDispatch({ type: 'SET_LOADING', payload: false });
          console.log('FINISH');
        });
    },
    [dispatch, logout, token],
  );

  const needToCallInit =
    token &&
    userLoggedIn &&
    !loginDataLoaded &&
    !isLoadingToken &&
    !calledInit &&
    !loginError &&
    !callingInit;

  useEffect(() => {
    if (!needToCallInit) return;

    // Guard asincrono: se un logout è in corso (flag doingLogout in localforage),
    // non procedere con l'init per evitare sessioni orfane
    const runInit = async () => {
      const logoutInProgress = await isDoingLogoutValid();
      if (logoutInProgress) {
        console.warn(
          '[needToCallInit] Logout in corso (doingLogout flag attivo), skip init.',
        );
        return;
      }

      initializeApiFactory(token);
      localDispatch({ type: 'SET_CALLING_INIT', payload: true });

      const tokenJWT = getDecodedJWTToken();
      if (!tokenJWT) {
        console.error('[needToCallInit] Token non decodificabile, skip init.');
        return;
      }
      const codiceAzienda = tokenJWT.workplaceRef;

      // ! Non bisogna salvare le chiavi in caso di impersonation
      if (tokenJWT.accessLevel === 'company') {
        localforage.setItem(
          'terminal_FOCUSCLOUD_workplaceRef',
          tokenJWT.consumerRef,
        );
      } else if (!getIsImpersonation())
        localforage.setItem('terminal_FOCUSCLOUD_workplaceRef', codiceAzienda);

      //!TODO in caso di gestione multipdv, bisogno impostare il workplaceRef con il codice multiPDV (consumerRef)

      callFOCUSInit({}, undefined);
    };

    runInit();
  }, [needToCallInit, token, callFOCUSInit, logout]);

  return {
    userLoggedIn,
    needOtpCode,
    isLoadingToken,
    calledInit,
    callFOCUSInit,
    loginError,
    needToChoosePDV,
    refreshToken,
    noSessionAvailable,
    inProgress,
  };
};

export default useLoginMsal;
